import React, { useState } from 'react';
import { IPreset } from '../../modules/reducers/presets';
import { InputItem } from '../../components';
import { DEFAULT_TRACKING_INPUT_DATA, PRESET_TRACKING_DATA } from '../../modules/constants/presets';
import { getTranslationKey } from '../../modules/utils/helpers';
import { get, partial } from 'lodash';

const GA4_PATTERN = /^G-[A-Z0-9]+$/;
const GTM_PATTERN = /^GTM-[A-Z0-9]+$/;

const getValidationError = (value: string, pattern: RegExp, format: string): string => {
  if (!value) {
    return '';
  }

  return pattern.test(value) ? '' : `Invalid format. Expected: ${format}`;
};

export const hasTrackingValidationErrors = (preset: Partial<IPreset>): boolean => {
  const { ga_id, gtm_id } = preset;

  return (
    (Boolean(ga_id) && !GA4_PATTERN.test(ga_id)) || (Boolean(gtm_id) && !GTM_PATTERN.test(gtm_id))
  );
};

interface IProps {
  preset: IPreset;
  onChangeCallback: (data: Partial<IPreset>) => void;
}

export const PresetTracking = ({ preset, onChangeCallback }: IProps) => {
  const { ga_id, gtm_id, fb_pixel_id } = preset;
  const ga = get(PRESET_TRACKING_DATA, 'ga_id', DEFAULT_TRACKING_INPUT_DATA);
  const gtm = get(PRESET_TRACKING_DATA, 'gtm_id', DEFAULT_TRACKING_INPUT_DATA);
  const fb_pixel = get(PRESET_TRACKING_DATA, 'fb_pixel_id', DEFAULT_TRACKING_INPUT_DATA);
  const changeFormHandler = (path: string, value: string | number | boolean) =>
    onChangeCallback({ [path]: value });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const handleBlur = (field: string) => () => setTouched((prev) => ({ ...prev, [field]: true }));

  const gaError = touched.ga_id ? getValidationError(ga_id, GA4_PATTERN, 'G-XXXXXXXXX') : '';
  const gtmError = touched.gtm_id ? getValidationError(gtm_id, GTM_PATTERN, 'GTM-XXXXXXX') : '';

  return (
    <div>
      <InputItem
        value={ga_id}
        placeholder={getTranslationKey(ga.placeholder)}
        withTooltip
        label={getTranslationKey(ga.title)}
        id={ga.tooltip_id}
        tooltipText={getTranslationKey(ga.tooltip_text)}
        changeCallback={partial(changeFormHandler, 'ga_id')}
        onBlurCallback={handleBlur('ga_id')}
        errorText={gaError}
      />
      <InputItem
        value={gtm_id}
        placeholder={getTranslationKey(gtm.placeholder)}
        withTooltip
        label={getTranslationKey(gtm.title)}
        id={gtm.tooltip_id}
        tooltipText={getTranslationKey(gtm.tooltip_text)}
        changeCallback={partial(changeFormHandler, 'gtm_id')}
        onBlurCallback={handleBlur('gtm_id')}
        errorText={gtmError}
      />
      <InputItem
        value={fb_pixel_id}
        placeholder={getTranslationKey(fb_pixel.placeholder)}
        withTooltip
        label={getTranslationKey(fb_pixel.title)}
        id={fb_pixel.tooltip_id}
        tooltipText={getTranslationKey(fb_pixel.tooltip_text)}
        changeCallback={partial(changeFormHandler, 'fb_pixel_id')}
      />
    </div>
  );
};
